import * as trace from 'owa-trace';
import calendarSelectionCompleted from '../actions/calendarSelectionCompleted';
import calendarSelectionInitiated from '../actions/calendarSelectionInitiated';
import datapoints from 'mini-calendar-store/lib/datapoints';
import getStore from '../store/store';
import toggleCalendarSelection from '../mutators/toggleCalendarSelection';
import updateUserConfigurationCalendarSelection from '../mutators/updateUserConfigurationCalendarSelection';
import { addCalendar, deleteCalendar } from 'mini-calendar-events-store';
import { AddCalendarResult } from 'mini-calendar-events-store/lib/api/addCalendar';
import { AppContext } from 'mini-app-context';
import { Calendar } from 'mini-event-to-calendar-association/lib/schema/Calendar';
import { getFilteredUserConfiguration } from 'mini-settings/lib/selectors/getFilteredUserConfiguration';
import { orchestrator } from 'satcheljs';
import { persistCtx, updateSelectedCalendars } from '../services/updateSelectedCalendars';
import {
    CALENDAR_UNSELECTED_AFTER_SELECTION,
    UNSELECT_DEFAULT_CAL,
} from 'mini-calendar-utils/lib/eventSignalConstants';

/**
 * Orchestrator which responds to calendar select/unselect done in calendar picker.
 */
const calendarSelectionInitiatedOrchestrator = orchestrator(
    calendarSelectionInitiated,
    async ({ calendarId, ctx }) => {
        await processCalendarSelectionUpdated(calendarId, ctx);
    }
);

export const processCalendarSelectionUpdated = async (calendarId: string, ctx?: AppContext) => {
    const { calendarIdToCalendarMapping, calendars, defaultCalendar } = getStore();
    const calendar: Calendar = calendarIdToCalendarMapping.get(calendarId);
    let selectedCalendarsLength = -1;

    if (calendar) {
        // We need to do this check before initiating the toggle.
        const selectedCalendars: Calendar[] = calendars.filter(cal => cal.isSelected);

        // As the default calendar only selected is lot of conditions, creating a separate flag here.
        const isDefaultCalendarOnlySelected =
            selectedCalendars.length === 1 &&
            defaultCalendar &&
            calendarId === defaultCalendar.id.id &&
            calendar.isSelected;

        if (isDefaultCalendarOnlySelected) {
            ctx?.signal(UNSELECT_DEFAULT_CAL);
            return;
        }

        selectedCalendarsLength = selectedCalendars.length;

        // Toggle the calendar selection
        toggleCalendarSelection(calendarId);

        // When calendar is selected, fetch the events from cache and add those items in surface.
        if (calendar.isSelected) {
            try {
                ctx?.signal(datapoints.CalendarStore.ADD_CALENDAR);
                const calendarAddedResult = await addCalendar(calendar);
                if (calendarAddedResult === AddCalendarResult.CALENDAR_NOT_ADDED) {
                    // This selection process no longer needs to be proceeded further as add did not happen.
                    ctx?.signal(CALENDAR_UNSELECTED_AFTER_SELECTION);
                    return;
                }
            } catch (error) {
                trace.errorThatWillCauseAlert(`[calendarSelectionInitiatedOrchestrator]: ${error}`);
                // TODO: Set the appropriate error related view state based on designer's input
            }
        } else {
            await deleteCalendar(calendar.calculatedFolderId);

            // Once we have done the unselection, we need to see if all the selected calendars are empty.
            // If yes, we need to trigger selection for default calendar.
            if (selectedCalendarsLength - 1 === 0) {
                ctx?.signal('ReselectDefaultCalendar');
                processCalendarSelectionUpdated(defaultCalendar.id.id, ctx);
            }
        }

        calendarSelectionCompleted();

        //Update user configuration local selection
        updateUserConfigurationCalendarSelection(calendarId, calendar.isSelected, ctx);

        //Update the user configuration remotely.
        persistCtx(ctx);
        updateSelectedCalendars(getFilteredUserConfiguration());
    }
};

export default calendarSelectionInitiatedOrchestrator;
